import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Plug, Zap, Users, MoonStar, ImagePlus, X, Pencil, CalendarX, Star, Search } from "lucide-react";
import api, { errText, fileUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export default function Settings() {
  const [wa, setWa] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentForm, setAgentForm] = useState({ name: "", email: "", employee_id: "", password: "", role: "agent" });
  const [qrs, setQrs] = useState([]);
  const [qrForm, setQrForm] = useState({ shortcut: "", text: "" });
  const [qrFile, setQrFile] = useState(null);
  const [qrEditing, setQrEditing] = useState(null);
  const [qrExistingMedia, setQrExistingMedia] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  const qrFileRef = useRef(null);
  const [autoReply, setAutoReply] = useState(null);
  const [arBusy, setArBusy] = useState(false);
  const [holidayInput, setHolidayInput] = useState("");
  const [qrSearch, setQrSearch] = useState("");

  const load = async () => {
    try {
      const [w, u, q, ar] = await Promise.all([api.get("/settings/whatsapp"), api.get("/users"), api.get("/quick-replies"), api.get("/settings/auto-reply")]);
      setWa(w.data);
      setAgents(u.data);
      setQrs(q.data);
      setAutoReply(ar.data);
    } catch (e) {}
  };

  useEffect(() => { load(); }, []);

  const testConnection = async () => {
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/whatsapp/test");
      setTestResult(data);
      toast.success(`Connected: ${data.phone} (${data.name})`);
    } catch (e) {
      setTestResult({ connected: false, error: errText(e) });
      toast.error(errText(e));
    } finally {
      setTestBusy(false);
    }
  };

  const toggleRR = async (enabled) => {
    try {
      await api.post("/settings/round-robin", { enabled });
      setWa((w) => ({ ...w, round_robin_enabled: enabled }));
      toast.success(`Round-robin ${enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(errText(e)); }
  };

  const toggleCsat = async (enabled) => {
    try {
      await api.post("/settings/csat", { enabled });
      setWa((w) => ({ ...w, csat_enabled: enabled }));
      toast.success(`CSAT ratings ${enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(errText(e)); }
  };

  const addAgent = async () => {
    try {
      await api.post("/users", agentForm);
      toast.success("Agent created");
      setAgentForm({ name: "", email: "", employee_id: "", password: "", role: "agent" });
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const toggleAgent = async (a) => {
    try {
      await api.patch(`/users/${a.id}`, { active: !a.active });
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const resetQrForm = () => {
    setQrForm({ shortcut: "", text: "" });
    setQrFile(null);
    setQrEditing(null);
    setQrExistingMedia(null);
    if (qrFileRef.current) qrFileRef.current.value = "";
  };

  const startEditQr = (q) => {
    setQrEditing(q.id);
    setQrForm({ shortcut: q.shortcut, text: q.text });
    setQrExistingMedia(q.media || null);
    setQrFile(null);
    if (qrFileRef.current) qrFileRef.current.value = "";
  };

  const addQr = async () => {
    setQrBusy(true);
    try {
      let media = {};
      if (qrFile) {
        const fd = new FormData();
        fd.append("file", qrFile);
        const { data } = await api.post("/uploads", fd);
        media = { media_file_id: data.file_id, media_mime: data.mime, media_filename: data.filename };
      } else if (qrExistingMedia) {
        media = { media_file_id: qrExistingMedia.file_id, media_mime: qrExistingMedia.mime, media_filename: qrExistingMedia.filename };
      }
      if (qrEditing) await api.put(`/quick-replies/${qrEditing}`, { ...qrForm, ...media });
      else await api.post("/quick-replies", { ...qrForm, ...media });
      toast.success(qrEditing ? "Quick message updated" : "Quick message saved");
      resetQrForm();
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setQrBusy(false);
    }
  };

  const saveAutoReply = async () => {
    setArBusy(true);
    try {
      await api.post("/settings/auto-reply", autoReply);
      toast.success("Auto-replies saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setArBusy(false);
    }
  };

  const backendHost = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="h-full overflow-y-auto p-6">
      <h1 className="font-heading text-2xl font-extrabold">API & Settings</h1>
      <div className="font-hand text-lg text-amber-800 mb-5">wire up your desk ✦</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="card-tactile card-blue p-5" data-testid="wa-settings-card">
            <div className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><Plug size={18} /> WhatsApp Business API</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="label-upper mb-0">Phone Number ID</span><span className="font-mono2">{wa?.phone_number_id}</span></div>
              <div className="flex justify-between"><span className="label-upper mb-0">WABA ID</span><span className="font-mono2">{wa?.waba_id}</span></div>
              <div className="flex justify-between"><span className="label-upper mb-0">Access Token</span><span className="font-mono2 text-xs">{wa?.token_masked}</span></div>
            </div>
            <button data-testid="test-connection-btn" className="btn-navy mt-3" onClick={testConnection} disabled={testBusy}>
              {testBusy ? "Testing..." : "Test Connection"}
            </button>
            {testResult && (
              <div className={`mt-2 text-sm font-semibold flex items-center gap-2 ${testResult.connected ? "text-green-700" : "text-red-700"}`} data-testid="test-connection-result">
                <span className={`w-2.5 h-2.5 rounded-full ${testResult.connected ? "bg-green-600" : "bg-red-600"}`} />
                {testResult.connected ? `${testResult.phone} · ${testResult.name} · quality: ${testResult.quality}` : testResult.error}
              </div>
            )}
          </div>

          <div className="card-tactile card-yellow p-5" data-testid="webhook-card">
            <div className="font-heading font-bold text-lg mb-2">Meta Webhook Setup</div>
            <div className="text-xs text-slate-700 mb-2">In Meta App Dashboard → WhatsApp → Configuration, set:</div>
            <div className="label-upper">Callback URL</div>
            <div className="font-mono2 text-xs bg-white rounded-lg border-2 p-2 break-all mb-2" style={{ borderColor: "var(--cd-navy)" }} data-testid="webhook-url">{backendHost}/api/webhook/whatsapp</div>
            <div className="label-upper">Verify Token</div>
            <div className="font-mono2 text-xs bg-white rounded-lg border-2 p-2 break-all" style={{ borderColor: "var(--cd-navy)" }} data-testid="verify-token">{wa?.verify_token}</div>
            <div className="text-xs text-slate-700 mt-2">Subscribe to the <b>messages</b> webhook field. Incoming chats will then appear in the Shared Inbox.</div>
          </div>

          <div className="card-tactile p-5 flex items-center justify-between" data-testid="round-robin-card">
            <div>
              <div className="font-heading font-bold flex items-center gap-2"><Users size={16} /> Round-Robin Auto Assignment</div>
              <div className="text-xs text-slate-600">New incoming chats are rotated across active agents</div>
            </div>
            <Switch data-testid="round-robin-toggle" checked={!!wa?.round_robin_enabled} onCheckedChange={toggleRR} />
          </div>

          <div className="card-tactile p-5 flex items-center justify-between" data-testid="csat-card">
            <div>
              <div className="font-heading font-bold flex items-center gap-2"><Star size={16} /> CSAT Rating on Resolve</div>
              <div className="text-xs text-slate-600">Customers get a 1-5 star rating menu on WhatsApp when a chat is resolved (within 24h window)</div>
            </div>
            <Switch data-testid="csat-toggle" checked={!!wa?.csat_enabled} onCheckedChange={toggleCsat} />
          </div>

          {!wa?.woo_configured && (
            <div className="card-tactile card-pink p-5">
              <div className="font-heading font-bold">WooCommerce (Order Details)</div>
              <div className="text-xs text-slate-700 mt-1">Not configured yet. Share your Store URL + Consumer Key/Secret with the developer to enable order lookup (WP Admin → WooCommerce → Settings → Advanced → REST API).</div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="card-tactile p-5" data-testid="agents-card">
            <div className="font-heading font-bold text-lg mb-3">Agent Management</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input data-testid="agent-name-input" className="input-tactile" placeholder="Name" value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} />
              <input data-testid="agent-empid-input" className="input-tactile" placeholder="Employee ID" value={agentForm.employee_id} onChange={(e) => setAgentForm({ ...agentForm, employee_id: e.target.value })} />
              <input data-testid="agent-email-input" className="input-tactile" placeholder="Email" value={agentForm.email} onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })} />
              <input data-testid="agent-password-input" className="input-tactile" type="password" placeholder="Password" value={agentForm.password} onChange={(e) => setAgentForm({ ...agentForm, password: e.target.value })} />
            </div>
            <div className="flex gap-2 items-center mb-3">
              <select data-testid="agent-role-select" className="input-tactile w-32" value={agentForm.role} onChange={(e) => setAgentForm({ ...agentForm, role: e.target.value })}>
                <option value="agent">Agent</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid="add-agent-btn" className="btn-navy" disabled={!agentForm.name || !agentForm.email || !agentForm.password} onClick={addAgent}><Plus size={15} /> Add</button>
            </div>
            <div className="space-y-1.5">
              {agents.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-black/[0.03] rounded-lg px-3 py-2" data-testid={`agent-row-${a.email}`}>
                  <div>
                    <span className="font-semibold text-sm">{a.name}</span>
                    <span className="text-xs text-slate-500 ml-2">{a.email}</span>
                    <span className="status-badge badge-pending ml-2">{a.role}</span>
                  </div>
                  <Switch checked={!!a.active} onCheckedChange={() => toggleAgent(a)} data-testid={`agent-active-toggle-${a.email}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="card-tactile card-green p-5" data-testid="quick-replies-card">
            <div className="font-heading font-bold text-lg flex items-center gap-2 mb-1"><Zap size={17} /> Quick Messages</div>
            <div className="font-hand text-base text-amber-800 mb-3">text, image or video — agents trigger with /</div>
            <div className="space-y-2 mb-3">
              <div className="flex gap-2">
                <input data-testid="qr-shortcut-input" className="input-tactile w-36" placeholder="/greeting" value={qrForm.shortcut} onChange={(e) => setQrForm({ ...qrForm, shortcut: e.target.value })} />
                <input ref={qrFileRef} type="file" accept="image/*,video/*" className="hidden" data-testid="qr-media-input" onChange={(e) => setQrFile(e.target.files?.[0] || null)} />
                <button data-testid="qr-attach-btn" className="btn-yellow px-3" onClick={() => qrFileRef.current?.click()} title="Attach image/video">
                  <ImagePlus size={15} /> {qrFile ? "Change" : "Media"}
                </button>
              </div>
              {qrFile && (
                <div className="flex items-center gap-2 text-xs font-semibold bg-white/70 border rounded-lg px-2 py-1.5" style={{ borderColor: "var(--cd-navy)" }} data-testid="qr-media-preview">
                  {qrFile.type.startsWith("image/") && <img src={URL.createObjectURL(qrFile)} alt="" className="w-10 h-10 rounded object-cover border" style={{ borderColor: "var(--cd-navy)" }} />}
                  <span className="truncate">{qrFile.name}</span>
                  <button className="btn-ghost2 py-0.5 ml-auto text-red-600" onClick={() => { setQrFile(null); if (qrFileRef.current) qrFileRef.current.value = ""; }}><X size={13} /></button>
                </div>
              )}
              {!qrFile && qrExistingMedia && (
                <div className="flex items-center gap-2 text-xs font-semibold bg-white/70 border rounded-lg px-2 py-1.5" style={{ borderColor: "var(--cd-navy)" }} data-testid="qr-existing-media">
                  {qrExistingMedia.mime?.startsWith("image/") && <img src={fileUrl(qrExistingMedia.file_id)} alt="" className="w-10 h-10 rounded object-cover border" style={{ borderColor: "var(--cd-navy)" }} />}
                  <span className="truncate">{qrExistingMedia.filename || "attached media"}</span>
                  <button data-testid="qr-remove-existing-media" className="btn-ghost2 py-0.5 ml-auto text-red-600" onClick={() => setQrExistingMedia(null)} title="Remove media"><X size={13} /></button>
                </div>
              )}
              <textarea data-testid="qr-text-input" className="input-tactile" rows={2} placeholder="Message text (used as caption when media is attached)..." value={qrForm.text} onChange={(e) => setQrForm({ ...qrForm, text: e.target.value })} />
              <div className="flex gap-2">
                <button data-testid="add-qr-btn" className="btn-navy" disabled={qrBusy || !qrForm.shortcut || (!qrForm.text && !qrFile && !qrExistingMedia)} onClick={addQr}>
                  {qrEditing ? <Pencil size={15} /> : <Plus size={15} />} {qrBusy ? "Saving..." : qrEditing ? "Update Quick Message" : "Save Quick Message"}
                </button>
                {qrEditing && <button data-testid="qr-cancel-edit-btn" className="btn-yellow" onClick={resetQrForm}>Cancel</button>}
              </div>
            </div>
            <div className="space-y-1.5">
              {qrs.length > 4 && (
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                  <input data-testid="settings-qr-search" className="input-tactile pl-8 py-1.5 text-sm" placeholder="Search quick messages..." value={qrSearch} onChange={(e) => setQrSearch(e.target.value)} />
                </div>
              )}
              {qrs.filter((q) => !qrSearch || q.shortcut.toLowerCase().includes(qrSearch.toLowerCase()) || (q.text || "").toLowerCase().includes(qrSearch.toLowerCase())).map((q) => (
                <div key={q.id} className="flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5 border" style={{ borderColor: "#E5DCB8" }}>
                  {q.media && (
                    q.media.mime?.startsWith("image/")
                      ? <img src={fileUrl(q.media.file_id)} alt="" className="w-8 h-8 rounded object-cover border shrink-0" style={{ borderColor: "var(--cd-navy)" }} />
                      : <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[var(--cd-blue)] border shrink-0" style={{ borderColor: "var(--cd-navy)" }}>{q.media.mime?.startsWith("video/") ? "VIDEO" : "FILE"}</span>
                  )}
                  <div className="text-sm min-w-0 truncate"><span className="font-mono2 font-bold">/{q.shortcut}</span> <span className="text-slate-600">— {q.text}</span></div>
                  <button data-testid={`edit-qr-${q.shortcut}`} className="btn-ghost2 py-1 ml-auto shrink-0" onClick={() => startEditQr(q)}><Pencil size={13} /></button>
                  <button data-testid={`delete-qr-${q.shortcut}`} className="btn-ghost2 py-1 text-red-600 shrink-0" onClick={async () => { await api.delete(`/quick-replies/${q.id}`); if (qrEditing === q.id) resetQrForm(); load(); }}><Trash2 size={13} /></button>
                </div>
              ))}
              {qrs.length === 0 && <div className="font-hand text-base text-amber-800">no quick messages yet — agents will thank you for these!</div>}
            </div>
          </div>

          <div className="card-tactile card-ochre p-5" data-testid="auto-reply-card">
            <div className="font-heading font-bold text-lg flex items-center gap-2 mb-1"><MoonStar size={17} /> Chat Auto-Replies</div>
            <div className="font-hand text-base text-amber-800 mb-3">welcome new customers · reply when the team is offline</div>
            {autoReply && (
              <div className="space-y-3">
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <label className="label-upper">Default business hours ({autoReply.timezone})</label>
                    <div className="flex gap-2 items-center">
                      <input data-testid="ar-start-input" type="time" className="input-tactile w-32" value={autoReply.hours_start} onChange={(e) => setAutoReply({ ...autoReply, hours_start: e.target.value })} />
                      <span className="text-sm font-bold">to</span>
                      <input data-testid="ar-end-input" type="time" className="input-tactile w-32" value={autoReply.hours_end} onChange={(e) => setAutoReply({ ...autoReply, hours_end: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 border rounded-lg p-3" style={{ borderColor: "var(--cd-navy)" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold">📅 Custom hours per day <span className="text-xs font-normal text-slate-600">(weekends etc.)</span></span>
                    <Switch data-testid="ar-perday-toggle" checked={!!autoReply.per_day_enabled} onCheckedChange={(v) => setAutoReply({ ...autoReply, per_day_enabled: v })} />
                  </div>
                  {autoReply.per_day_enabled && (
                    <div className="space-y-1">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((name, i) => {
                        const d = autoReply.days?.[String(i)] || { closed: false, start: autoReply.hours_start, end: autoReply.hours_end };
                        const setDay = (patch) => setAutoReply({ ...autoReply, days: { ...(autoReply.days || {}), [String(i)]: { ...d, ...patch } } });
                        return (
                          <div key={name} className="flex items-center gap-2" data-testid={`ar-day-row-${name.toLowerCase()}`}>
                            <span className="w-9 text-xs font-bold">{name}</span>
                            <Switch data-testid={`ar-day-open-${name.toLowerCase()}`} checked={!d.closed} onCheckedChange={(v) => setDay({ closed: !v })} />
                            {d.closed ? (
                              <span className="text-xs font-semibold text-slate-500">Closed — away message all day</span>
                            ) : (
                              <>
                                <input type="time" className="input-tactile w-28 py-1 text-xs" value={d.start} onChange={(e) => setDay({ start: e.target.value })} />
                                <span className="text-xs font-bold">–</span>
                                <input type="time" className="input-tactile w-28 py-1 text-xs" value={d.end} onChange={(e) => setDay({ end: e.target.value })} />
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="bg-white/70 border rounded-lg p-3" style={{ borderColor: "var(--cd-navy)" }}>
                  <div className="text-sm font-bold flex items-center gap-1.5 mb-1.5"><CalendarX size={14} /> Holidays <span className="text-xs font-normal text-slate-600">(away message all day)</span></div>
                  <div className="flex gap-2 mb-1.5">
                    <input data-testid="ar-holiday-input" type="date" className="input-tactile w-44 py-1 text-xs" value={holidayInput} onChange={(e) => setHolidayInput(e.target.value)} />
                    <button data-testid="ar-holiday-add-btn" className="btn-yellow py-1 px-2 text-xs" disabled={!holidayInput} onClick={() => { if (!autoReply.holidays?.includes(holidayInput)) setAutoReply({ ...autoReply, holidays: [...(autoReply.holidays || []), holidayInput].sort() }); setHolidayInput(""); }}>
                      <Plus size={13} /> Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(autoReply.holidays || []).map((h) => (
                      <span key={h} className="text-xs font-bold px-2 py-0.5 rounded-full border-2 bg-white inline-flex items-center gap-1" style={{ borderColor: "var(--cd-navy)" }} data-testid={`ar-holiday-${h}`}>
                        {h}
                        <button className="text-red-600" onClick={() => setAutoReply({ ...autoReply, holidays: autoReply.holidays.filter((x) => x !== h) })}><X size={11} /></button>
                      </span>
                    ))}
                    {(autoReply.holidays || []).length === 0 && <span className="text-xs text-slate-500">No holidays added</span>}
                  </div>
                </div>
                <div className="bg-white/70 border rounded-lg p-3" style={{ borderColor: "var(--cd-navy)" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold">👋 Welcome message <span className="text-xs font-normal text-slate-600">(first message, within hours)</span></span>
                    <Switch data-testid="ar-welcome-toggle" checked={!!autoReply.welcome_enabled} onCheckedChange={(v) => setAutoReply({ ...autoReply, welcome_enabled: v })} />
                  </div>
                  <textarea data-testid="ar-welcome-text" className="input-tactile" rows={2} placeholder="Hi! Thanks for reaching out to StarAndDaisy — an agent will be with you shortly." value={autoReply.welcome_text} onChange={(e) => setAutoReply({ ...autoReply, welcome_text: e.target.value })} />
                </div>
                <div className="bg-white/70 border rounded-lg p-3" style={{ borderColor: "var(--cd-navy)" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold">🌙 Away message <span className="text-xs font-normal text-slate-600">(outside hours, max once / 6h)</span></span>
                    <Switch data-testid="ar-away-toggle" checked={!!autoReply.away_enabled} onCheckedChange={(v) => setAutoReply({ ...autoReply, away_enabled: v })} />
                  </div>
                  <textarea data-testid="ar-away-text" className="input-tactile" rows={2} placeholder="We're currently offline. Our team is available 9am–6pm IST and will reply first thing!" value={autoReply.away_text} onChange={(e) => setAutoReply({ ...autoReply, away_text: e.target.value })} />
                </div>
                <button data-testid="ar-save-btn" className="btn-navy" disabled={arBusy} onClick={saveAutoReply}>{arBusy ? "Saving..." : "Save Auto-Replies"}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
